import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, CheckCircle, Edit, Map } from "@mui/icons-material";
import styles from '../../assets/styles/LayoutShared.module.css';
import api from '../../services/api';
import { formatarDataBR } from '../../utils/formatters';
import RegistroForm from './RegistroForm';
import ValidacaoMapaModal from './ValidacaoMapaModal';

const AValidar = () => {
  const navigate = useNavigate();
  const [registros, setRegistros] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [confirmando, setConfirmando] = useState<Record<string, boolean>>({});
  const [erro, setErro] = useState<Record<string, string>>({});

  const [registroEditandoUid, setRegistroEditandoUid] = useState<string | null>(null);
  const [registroNoMapaUid, setRegistroNoMapaUid] = useState<string | null>(null);

  const fetchPendentes = useCallback(() => {
    setCarregando(true);
    api.get('/pesca/registros/pendentes-validacao')
      .then((res) => setRegistros(res.data.data))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { fetchPendentes(); }, [fetchPendentes]);

  const handleConfirmar = async (r: any) => {
    // Antes validava direto no clique — 1 clique errado validava um
    // registro sem chance de desfazer. Agora exige confirmação explícita.
    if (!window.confirm('Confirmar a validação deste registro de pesca?')) return;
    setConfirmando((prev) => ({ ...prev, [r.uid_pesca_registro]: true }));
    setErro((prev) => ({ ...prev, [r.uid_pesca_registro]: '' }));
    try {
      await api.post(`/pesca/registros/${r.uid_pesca_registro}/validar`);
      setRegistros((prev) => prev.filter((x) => x.uid_pesca_registro !== r.uid_pesca_registro));
    } catch (err: any) {
      setErro((prev) => ({ ...prev, [r.uid_pesca_registro]: err?.response?.data?.message || 'Erro ao validar.' }));
    } finally {
      setConfirmando((prev) => ({ ...prev, [r.uid_pesca_registro]: false }));
    }
  };

  const handleRegistroEditado = () => {
    setRegistroEditandoUid(null);
    fetchPendentes();
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>A Validar</h1>
        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{registros.length}</span>
          </p>
          <button className={styles.blueBtn} onClick={() => navigate(-1)} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-8px', marginBottom: '16px' }}>
        Registros pendentes de validação nos projetos em que você é validador.
      </p>

      {carregando ? (
        <p style={{ color: '#999' }}>Carregando...</p>
      ) : registros.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', textAlign: 'center', color: '#999' }}>
          Nenhum registro pendente de validação no momento.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {registros.map((r) => {
            const peso = parseFloat(r.dataValues?.peso_calculado ?? r.peso_calculado ?? 0);
            const quantidade = parseFloat(r.dataValues?.quantidade_calculada ?? r.quantidade_calculada ?? 0);
            const pesoTexto = peso === 0 && quantidade > 0 ? `${quantidade} un` : `${peso.toFixed(2)} kg`;
            const local = r.uid_pesca_local_tbl_pesca_local
              ? r.uid_pesca_local_tbl_pesca_local.nome
              : r.descricao_local
                ? `${r.descricao_local} — pendente de classificação`
                : 'Local não informado';

            return (
              <div
                key={r.uid_pesca_registro}
                style={{ background: '#fff', borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', cursor: 'pointer' }}
                onClick={() => navigate(`/admin/registros/view/${r.uid_pesca_registro}`)}
                title="Ver detalhes do registro"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>{r.pescador?.nome || 'Pescador não identificado'}</strong>
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '3px' }}>
                      {r.projeto?.nome || '-'} · {formatarDataBR(r.data_inicio)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                      {local} · <strong>{pesoTexto}</strong>
                    </div>
                  </div>

                  {/* stopPropagation nos botões — clicar num ícone não deve
                      também disparar a navegação do card pra tela de detalhes. */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <button style={iconBtnStyle} title="Ver local no mapa" onClick={() => setRegistroNoMapaUid(r.uid_pesca_registro)}>
                      <Map fontSize="small" />
                    </button>
                    <button style={iconBtnStyle} title="Editar registro" onClick={() => setRegistroEditandoUid(r.uid_pesca_registro)}>
                      <Edit fontSize="small" />
                    </button>
                    <button
                      style={{ ...iconBtnStyle, background: '#e6fbf3', borderColor: '#00b37e', color: '#00b37e' }}
                      title="Confirmar validação"
                      disabled={confirmando[r.uid_pesca_registro]}
                      onClick={() => handleConfirmar(r)}
                    >
                      <CheckCircle fontSize="small" />
                    </button>
                  </div>
                </div>

                {erro[r.uid_pesca_registro] && (
                  <p style={{ color: '#f75a68', fontSize: '0.8rem', margin: '8px 0 0' }}>{erro[r.uid_pesca_registro]}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {registroEditandoUid && (
        <RegistroForm
          registroUid={registroEditandoUid}
          onClose={() => setRegistroEditandoUid(null)}
          onSaved={handleRegistroEditado}
        />
      )}

      {registroNoMapaUid && (
        <ValidacaoMapaModal
          uidRegistro={registroNoMapaUid}
          onClose={() => setRegistroNoMapaUid(null)}
        />
      )}
    </div>
  );
};

const iconBtnStyle: React.CSSProperties = {
  width: '34px', height: '34px', borderRadius: '50%', border: '1px solid #ddd', background: '#fff',
  color: '#03c9fe', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default AValidar;
